
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Folder, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Folder {
  id: string;
  name: string;
}

interface FolderSelectProps {
  value?: string;
  onSelect: (folderId: string | null) => void;
}

export function FolderSelect({ value, onSelect }: FolderSelectProps) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchFolders();
    } else {
      setLoading(false);
      setFolders([]);
    }
  }, [user]);

  const fetchFolders = async () => {
    if (!user) return;
    
    try {
      console.log("Fetching folders for user:", user.id);
      const { data, error } = await supabase
        .from("folders")
        .select("id, name")
        .eq('user_id', user.id)
        .order("name");

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Fetched folders:", data);
      setFolders(data || []);
    } catch (error: any) {
      console.error("Error fetching folders:", error);
      toast({
        title: "Fout bij ophalen",
        description: error.message || "Er is een fout opgetreden bij het ophalen van de mappen.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !user) {
      if (!user) {
        toast({
          title: "Niet ingelogd",
          description: "Je moet ingelogd zijn om een map aan te maken.",
          variant: "destructive",
        });
      }
      return;
    }

    try {
      console.log("Creating folder for user:", user.id);
      const { data, error } = await supabase
        .from("folders")
        .insert({ 
          name: newFolderName.trim(),
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Created folder:", data);
      setFolders(prev => [...prev, data]);
      setNewFolderName("");
      setIsOpen(false);
      toast({
        title: "Map aangemaakt",
        description: "De nieuwe map is succesvol aangemaakt.",
      });
    } catch (error: any) {
      console.error("Error creating folder:", error);
      toast({
        title: "Fout bij aanmaken",
        description: error.message || "Er is een fout opgetreden bij het aanmaken van de map.",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return <div>Laden...</div>;
  }

  return (
    <div className="flex gap-2">
      <Select value={value || "all"} onValueChange={(val) => onSelect(val === "all" ? null : val)}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Selecteer map" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              Alle plattegronden
            </div>
          </SelectItem>
          {folders.map((folder) => (
            <SelectItem key={folder.id} value={folder.id}>
              <div className="flex items-center gap-2">
                <Folder className="h-4 w-4" />
                {folder.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe map aanmaken</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="Mapnaam"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateFolder();
                }
              }}
            />
            <Button onClick={handleCreateFolder} className="w-full">
              Map aanmaken
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
